/* eslint-disable ember/classic-decorator-no-classic-methods, ember/no-computed-properties-in-native-classes, ember/no-get, ember/require-computed-property-dependencies, warp-drive/no-legacy-request-patterns */
// Extend From
import Controller from '@ember/controller';
import { tracked } from '@glimmer/tracking';
// Ember Imports
import { get, set, action, computed } from '@ember/object';
import { service } from '@ember/service';
import { isEmpty } from '@ember/utils';
// External Imports
import moment from 'moment';
// Internal Imports
import DataType from '@linc-technologies/linced-common/enums/data-type';
import Scopes from '@linc-technologies/linced-common/enums/scopes';
import { findBy } from '@linc-technologies/linced-common/utils/arrays';
import {
  filterValue,
  getNumericYearLevelFilter,
} from '@linc-technologies/linced-common/utils/filtering';
import { generatePost } from 'linced-app/utils/incidents';
import { optionAlias } from '@linc-technologies/linced-common/utils/options';
import { saveOrDiscard } from '@linc-technologies/linced-common/utils/record-rollback';
import { defaultTinyMCEConfig } from '@linc-technologies/linced-common/utils/tinymce';
import {
  BehaviourType,
  IncidentOutcome,
  IncidentStatus,
  BehaviourCategoriesNSW,
} from 'linced-app/enums/behaviours';
import ExpulsionStage from 'linced-app/enums/expulsion-stage';

// loadIncidentPeople resolves the incident's learner, school and referred-to
// person.
function loadIncidentPeople(incident) {
  return Promise.all([
    incident.learnerId,
    incident.schoolId,
    incident.referredTo,
  ]);
}

export default class IncidentsController extends Controller {
  // Services
  @service
  countdownButton;

  @service
  fieldOptions;

  @service
  options;

  @service
  roles;

  @service
  router;

  @service
  session;

  @service
  store;

  @service
  toaster;

  BehaviourType = BehaviourType;
  BehaviourCategoriesNSW = BehaviourCategoriesNSW;
  IncidentStatus = IncidentStatus;
  IncidentOutcome = IncidentOutcome;
  Scopes = Scopes;

  // Attrs
  // queryParams provides a binding to the routes query params.
  queryParams = ['dateFrom', 'dateTo', 'termId'];

  // State
  // dateFrom provides the stateful binding to a query param.
  @tracked
  dateFrom = moment().startOf('year').format('YYYY-MM-DD');

  // dateTo provides the stateful binding to a query param.
  @tracked
  dateTo = moment().endOf('year').format('YYYY-MM-DD');

  // today provides today's date, for comparison purposes.
  today = moment().format('YYYY-MM-DD');

  @tracked
  filters = [];
  @tracked
  behaviourPost = null;
  @tracked
  detailsDialogOpen = false;
  @tracked
  incidentToDelete = null;
  editorOptions = Object.freeze(defaultTinyMCEConfig());
  @optionAlias('hero:features:expulsions')
  expulsionsEnabled;

  @tracked
  incidentToAction;

  @tracked
  processing;

  @tracked
  incidentToClose;

  @tracked
  termId;

  @computed('session.activeSchool', 'fieldOptions.yearLevelOptions.[]')
  get yearLevelOptions() {
    let options = findBy(
      this.get('fieldOptions.yearLevelOptions'),
      'schoolId',
      this.get('session.activeSchool'),
    );
    if (isEmpty(options) || isEmpty(options.options)) {
      return [];
    }
    return options.options;
  }

  @computed('model.behaviourIncidents.[]', 'filters.@each.{field,value}')
  get filteredBehaviourIncidents() {
    if (isEmpty(this.filters)) {
      // No filters - just return the data.
      return this.model.behaviourIncidents;
    }

    // Determine datatypes for any specified filters.
    let filters = this.filters.map((filter) => {
      let dataType = DataType.STRING;
      return {
        key: filter.field,
        dataType,
        value: filter.value,
      };
    });

    // Determine numeric filter for year level, if applicable.
    let yearLevelFilterValue = getNumericYearLevelFilter(
      findBy(filters, 'key', 'yearLevel'),
      this.yearLevelOptions,
    );

    // Apply all filters to each row in one hit, to avoid iterating over a
    // (potentially) large dataset multiple times.
    return this.model.behaviourIncidents.filter((row) => {
      return filters.every((filter) => {
        // Field-specific filter processing.
        switch (filter.key) {
          case 'yearLevel':
            return filterValue(
              row.get('learner.numericCurrentYearLevel') ?? '',
              DataType.INTEGER,
              yearLevelFilterValue,
            );
        }

        // Generic filter processing.
        return filterValue(
          get(row, filter.key) ?? '',
          filter.dataType,
          filter.value,
        );
      });
    });
  }

  @computed('model.expulsions.[]')
  get peopleWithCurrentExpulsions() {
    if (isEmpty(this.model.expulsions)) {
      return [];
    }
    // Use a set just in case there is somehow multiple expulsions.
    let people = new Set();
    this.model.expulsions.forEach((expulsion) => {
      if (expulsion.stage === ExpulsionStage.NOT_EXPELLED) {
        return;
      }
      people.add(expulsion.get('learnerId.id'));
    });

    return [...people];
  }

  /**
   * onChangeDateFrom changes the dateFrom field and removes the currently
   * set term as the date has changed, therefore not explicitly the currently
   * set term.
   *
   * @param {string} newDate - The new dateFrom to change the value to.
   */
  @action
  onChangeDateFrom(newDate) {
    this.dateFrom = newDate;
    this.termId = null;
  }

  /**
   * onChangeDateTo changes the dateTo field and removes the currently set
   * term as the date has changed.
   *
   * @param {string} newDate - The new dateFrom to change the value to.
   */
  @action
  onChangeDateTo(newDate) {
    this.dateTo = newDate;
    this.termId = null;
  }

  /**
   * setSelectedTermId sets the query params, which in turn will refresh the
   * model.
   *
   * @param {Object} term - A term record.
   */
  @action
  setSelectedTermId(term) {
    this.dateFrom = term.start;
    this.dateTo = term.end;
    this.termId = term.id;
  }

  @action
  filter(field, value) {
    let filter = findBy(this.filters, 'field', field);
    if (isEmpty(filter)) {
      // No existing filter on this field; add it.
      this.filters = [...this.filters, { field, value }];
      return;
    }

    if (isEmpty(value)) {
      // Filter has been cleared; remove it.
      this.filters = this.filters.filter((f) => f !== filter);
    } else {
      // Existing filter has been changed; update it.
      set(filter, 'value', value);
    }
  }

  @action
  openDetailsDialog(incident) {
    let post = incident.get('post');
    this.behaviourPost = post.content;
    this.detailsDialogOpen = true;
  }

  @action
  openDeleteDialog(incident) {
    this.detailsDialogOpen = true;
    this.incidentToDelete = incident;
    this.countdownButton.initialise(3);
  }

  @action
  closeDetailsDialog() {
    this.behaviourPost = null;
    this.detailsDialogOpen = false;
    this.incidentToDelete = null;
    this.incidentToClose = null;
    this.countdownButton.cleanup();
  }

  @action
  closeFurtherActionsDialog() {
    this.incidentToAction = null;
  }

  @action
  openCloseIncidentDialog(incident) {
    this.detailsDialogOpen = true;
    this.incidentToClose = incident;
  }

  @action
  async createExpulsion(incident) {
    // The loaded records, not the belongsTo proxies, which createRecord rejects.
    let learner, school, referredTo;
    try {
      [learner, school, referredTo] = await loadIncidentPeople(incident);
    } catch {
      this.toaster.error('Error creating expulsion');
      return;
    }
    let expulsion = this.store.createRecord('attendance/expulsion', {
      authorId: this.get('session.person'),
      date: moment().format('YYYY-MM-DD'),
      incidentId: incident,
      learnerId: learner,
      schoolId: school,
      nominatedSchoolPerson: referredTo,
      factualDetailsOfBehavioursOfConcern: incident.entryNote,
      jurisdiction: this.get('session.school.jurisdiction'),
    });

    try {
      // A failed create is discarded, so the incident doesn't keep pointing at it.
      await saveOrDiscard(expulsion);
    } catch {
      this.toaster.error('Error creating expulsion');
      return;
    }

    incident.set('outcome', IncidentOutcome.EXPULSION);
    incident.set('status', IncidentStatus.CLOSED);
    await incident.save();
    this.router.transitionTo('wellbeing.expulsions', {
      queryParams: { highlightedExpulsionId: expulsion.id },
    });
  }

  @action
  async createSuspension(incident) {
    let learner, school, referredTo;
    try {
      [learner, school, referredTo] = await loadIncidentPeople(incident);
    } catch {
      this.toaster.error('Error creating suspension');
      return;
    }
    let suspension = this.store.createRecord('attendance/suspension', {
      schoolId: school,
      behaviourDetails: incident.entryNote,
      reason: incident.category,
      writtenReason: incident.customCategory,
      learnerId: learner,
      recordedDate: moment().format('YYYY-MM-DD'),
      behaviourIncident: incident,
      nominatedContact: referredTo,
      jurisdiction: this.get('session.school.jurisdiction'),
    });

    try {
      await saveOrDiscard(suspension);
    } catch {
      this.toaster.error('Error creating suspension');
      return;
    }

    incident.set('outcome', IncidentOutcome.SUSPENSION);
    incident.set('status', IncidentStatus.CLOSED);
    await incident.save();
    this.router.transitionTo('wellbeing.suspensions', {
      queryParams: {
        highlightedSuspension: suspension.id,
        selectedTab: suspension.tab,
      },
    });
  }

  @action
  closeIncident() {
    this.processing = true;
    let incident = this.incidentToClose;
    incident.setProperties({
      outcome: IncidentOutcome.INCIDENT_CLOSED,
      status: IncidentStatus.CLOSED,
    });
    if (incident.get('post.id')) {
      incident.get('post').then((post) => {
        let content = generatePost(incident);
        post.set('content', content);
        return post.save().catch(() => {
          this.toaster.error('Error updating incident post');
        });
      });
    }
    incident
      .save()
      .then(() => {
        this.toaster.success('Behaviour Incident closed succesfully');
      })
      .catch(() => {
        this.toaster.error('Error closing Behaviour Incident');
      })
      .finally(() => {
        this.processing = false;
        this.closeDetailsDialog();
      });
  }

  @action
  deleteIncident() {
    this.processing = true;
    return Promise.resolve()
      .then(() => {
        if (this.incidentToDelete.get('post.id')) {
          return this.incidentToDelete
            .get('post')
            .then((post) => {
              return post.destroyRecord();
            })
            .catch(() => {
              return Promise.resolve();
            });
        }
      })
      .then(() => {
        return this.incidentToDelete.destroyRecord();
      })
      .then(() => {
        this.toaster.success('Behaviour Incident deleted succesfully');
      })
      .catch(() => {
        this.toaster.error('Error deleting Behaviour Incident');
      })
      .finally(() => {
        this.processing = false;
        this.closeDetailsDialog();
      });
  }

  @action
  viewOutcome(incident) {
    let route =
      incident.outcome === IncidentOutcome.EXPULSION
        ? 'wellbeing.expulsions'
        : 'wellbeing.suspensions';

    return this.router.transitionTo(route, {
      queryParams:
        incident.outcome === IncidentOutcome.EXPULSION
          ? { highlightedExpulsionId: incident.get('expulsion.id') }
          : {
              highlightedSuspension: incident.get('suspension.id'),
              selectedTab: incident.get('suspension.tab'),
            },
    });
  }
}
